'use client';

import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import { formatPrice } from '@/lib/utils';

export interface ProductItem {
  id: number;
  name: string;
  slug: string;
  description: string;
  price: number | string;
  comparePrice?: number | string | null;
  franchise: string;
  featured: boolean;
  isNew: boolean;
  categoryId: number;
  category: { id: number; name: string };
  images: { id: number; url: string; alt: string }[];
}

export interface CategoryOption {
  id: number;
  name: string;
}

interface ProductsManagerProps {
  products: ProductItem[];
  categories: CategoryOption[];
}

export function ProductsManager({ products, categories }: ProductsManagerProps): React.JSX.Element {
  const router = useRouter();

  // Estado para crear producto
  const [isCreating, setIsCreating] = useState(false);
  const [newProd, setNewProd] = useState({
    name: '',
    description: '',
    price: '',
    comparePrice: '',
    franchise: 'Dragon Ball',
    categoryId: categories[0]?.id || 1,
    imageUrl: '',
    featured: false,
    isNew: true,
  });

  // Estado para editar producto existente
  const [editingProd, setEditingProd] = useState<ProductItem | null>(null);
  const [editImageUrl, setEditImageUrl] = useState('');

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  // Crear Producto
  const handleCreateProduct = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      const res = await fetch('/api/products', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...newProd,
          price: parseFloat(newProd.price),
          comparePrice: newProd.comparePrice ? parseFloat(newProd.comparePrice) : null,
          categoryId: Number(newProd.categoryId),
        }),
      });

      if (!res.ok) throw new Error('Error al crear producto');

      setMessage('¡Producto agregado con éxito al catálogo!');
      setIsCreating(false);
      setNewProd({
        name: '',
        description: '',
        price: '',
        comparePrice: '',
        franchise: 'Dragon Ball',
        categoryId: categories[0]?.id || 1,
        imageUrl: '',
        featured: false,
        isNew: true,
      });
      router.refresh();
    } catch {
      setMessage('Error al guardar el producto.');
    } finally {
      setLoading(false);
    }
  };

  // Guardar Edición
  const handleUpdateProduct = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!editingProd) return;

    setLoading(true);
    setMessage(null);

    try {
      const res = await fetch(`/api/products/${editingProd.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: editingProd.name,
          description: editingProd.description,
          price: parseFloat(String(editingProd.price)),
          comparePrice: editingProd.comparePrice ? parseFloat(String(editingProd.comparePrice)) : null,
          franchise: editingProd.franchise,
          categoryId: Number(editingProd.categoryId),
          featured: editingProd.featured,
          isNew: editingProd.isNew,
          imageUrl: editImageUrl || undefined,
        }),
      });

      if (!res.ok) throw new Error('Error al actualizar');

      setMessage('¡Producto actualizado correctamente!');
      setEditingProd(null);
      router.refresh();
    } catch {
      setMessage('Error al actualizar producto.');
    } finally {
      setLoading(false);
    }
  };

  // Eliminar Producto
  const handleDeleteProduct = async (id: number): Promise<void> => {
    if (!confirm('¿Seguro que deseas eliminar este producto del catálogo?')) return;

    setLoading(true);
    try {
      const res = await fetch(`/api/products/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Error al eliminar');
      setMessage('Producto eliminado.');
      router.refresh();
    } catch {
      setMessage('Error al eliminar producto.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Cabecera y Botón de Nuevo Producto */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-white">Inventario de Productos</h2>
          <p className="text-xs text-slate-400">Total en catálogo: {products.length} artículos</p>
        </div>
        <div className="flex items-center space-x-3">
          {message && (
            <span className="text-xs font-mono text-emerald-400 bg-emerald-500/10 px-3 py-1 rounded-md border border-emerald-500/20">
              {message}
            </span>
          )}
          <button
            onClick={() => setIsCreating(!isCreating)}
            className="px-4 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-black font-bold text-xs flex items-center space-x-1.5 cursor-pointer shadow-lg transition-transform hover:scale-105"
          >
            <span>{isCreating ? '✖ Cerrar Formulario' : '➕ Agregar Producto'}</span>
          </button>
        </div>
      </div>

      {/* Formulario de Crear Producto */}
      {isCreating && (
        <form onSubmit={handleCreateProduct} className="p-6 rounded-2xl bg-[#12111F]/90 border border-[#2A2545] space-y-4">
          <h3 className="text-sm font-bold text-amber-400 uppercase tracking-wider">Nuevo Producto</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 text-xs">
            <div>
              <label className="text-slate-400 block mb-1">Nombre</label>
              <input
                type="text"
                required
                value={newProd.name}
                onChange={(e) => setNewProd({ ...newProd, name: e.target.value })}
                placeholder="Ej: Remera Goku SSJ"
                className="w-full p-2.5 rounded-lg bg-black/60 border border-[#2A2545] text-white focus:outline-none focus:border-amber-400"
              />
            </div>

            <div>
              <label className="text-slate-400 block mb-1">Franquicia</label>
              <input
                type="text"
                required
                value={newProd.franchise}
                onChange={(e) => setNewProd({ ...newProd, franchise: e.target.value })}
                placeholder="Ej: Dragon Ball, One Piece..."
                className="w-full p-2.5 rounded-lg bg-black/60 border border-[#2A2545] text-white focus:outline-none focus:border-amber-400"
              />
            </div>

            <div>
              <label className="text-slate-400 block mb-1">Categoría</label>
              <select
                value={newProd.categoryId}
                onChange={(e) => setNewProd({ ...newProd, categoryId: Number(e.target.value) })}
                className="w-full p-2.5 rounded-lg bg-black/60 border border-[#2A2545] text-white focus:outline-none focus:border-amber-400"
              >
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-slate-400 block mb-1">Precio ($ ARS)</label>
              <input
                type="number"
                step="0.01"
                required
                value={newProd.price}
                onChange={(e) => setNewProd({ ...newProd, price: e.target.value })}
                placeholder="9500"
                className="w-full p-2.5 rounded-lg bg-black/60 border border-[#2A2545] text-white focus:outline-none focus:border-amber-400"
              />
            </div>

            <div>
              <label className="text-slate-400 block mb-1">Precio Anterior / Tachado ($)</label>
              <input
                type="number"
                step="0.01"
                value={newProd.comparePrice}
                onChange={(e) => setNewProd({ ...newProd, comparePrice: e.target.value })}
                placeholder="Opcional"
                className="w-full p-2.5 rounded-lg bg-black/60 border border-[#2A2545] text-white focus:outline-none focus:border-amber-400"
              />
            </div>

            <div>
              <label className="text-slate-400 block mb-1">URL de la Imagen</label>
              <input
                type="url"
                value={newProd.imageUrl}
                onChange={(e) => setNewProd({ ...newProd, imageUrl: e.target.value })}
                placeholder="https://..."
                className="w-full p-2.5 rounded-lg bg-black/60 border border-[#2A2545] text-white focus:outline-none focus:border-amber-400"
              />
            </div>

            <div className="sm:col-span-2 md:col-span-3">
              <label className="text-slate-400 block mb-1">Descripción</label>
              <textarea
                required
                rows={2}
                value={newProd.description}
                onChange={(e) => setNewProd({ ...newProd, description: e.target.value })}
                placeholder="Detalles del producto, talle, material..."
                className="w-full p-2.5 rounded-lg bg-black/60 border border-[#2A2545] text-white focus:outline-none focus:border-amber-400"
              />
            </div>

            <div className="flex items-center space-x-6 sm:col-span-2">
              <label className="flex items-center space-x-2 text-slate-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={newProd.featured}
                  onChange={(e) => setNewProd({ ...newProd, featured: e.target.checked })}
                  className="rounded bg-black border-[#2A2545]"
                />
                <span>Destacado (Efecto Kaio-ken)</span>
              </label>

              <label className="flex items-center space-x-2 text-slate-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={newProd.isNew}
                  onChange={(e) => setNewProd({ ...newProd, isNew: e.target.checked })}
                  className="rounded bg-black border-[#2A2545]"
                />
                <span>Badge Nuevo</span>
              </label>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full sm:w-auto px-6 py-2.5 rounded-xl bg-amber-400 hover:bg-amber-300 text-black font-bold text-xs cursor-pointer"
          >
            {loading ? 'Guardando...' : 'Guardar y Publicar en la Tienda'}
          </button>
        </form>
      )}

      {/* Modal / Formulario de Edición */}
      {editingProd && (
        <form onSubmit={handleUpdateProduct} className="p-6 rounded-2xl bg-[#1A1830] border-2 border-amber-400/80 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold text-amber-400 uppercase tracking-wider">
              Editar: {editingProd.name}
            </h3>
            <button
              type="button"
              onClick={() => setEditingProd(null)}
              className="text-xs text-slate-400 hover:text-white"
            >
              ✖ Cancelar
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 text-xs">
            <div>
              <label className="text-slate-400 block mb-1">Nombre</label>
              <input
                type="text"
                required
                value={editingProd.name}
                onChange={(e) => setEditingProd({ ...editingProd, name: e.target.value })}
                className="w-full p-2 rounded-lg bg-black/60 border border-[#2A2545] text-white"
              />
            </div>

            <div>
              <label className="text-slate-400 block mb-1">Precio ($ ARS)</label>
              <input
                type="number"
                step="0.01"
                required
                value={editingProd.price}
                onChange={(e) => setEditingProd({ ...editingProd, price: e.target.value })}
                className="w-full p-2 rounded-lg bg-black/60 border border-[#2A2545] text-white"
              />
            </div>

            <div>
              <label className="text-slate-400 block mb-1">Categoría</label>
              <select
                value={editingProd.categoryId}
                onChange={(e) => setEditingProd({ ...editingProd, categoryId: Number(e.target.value) })}
                className="w-full p-2 rounded-lg bg-black/60 border border-[#2A2545] text-white"
              >
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="sm:col-span-2">
              <label className="text-slate-400 block mb-1">Nueva URL de Imagen (opcional para cambiar)</label>
              <input
                type="url"
                value={editImageUrl}
                onChange={(e) => setEditImageUrl(e.target.value)}
                placeholder="Dejar vacío para conservar la imagen actual"
                className="w-full p-2 rounded-lg bg-black/60 border border-[#2A2545] text-white"
              />
            </div>

            <div>
              <label className="text-slate-400 block mb-1">Franquicia</label>
              <input
                type="text"
                value={editingProd.franchise}
                onChange={(e) => setEditingProd({ ...editingProd, franchise: e.target.value })}
                className="w-full p-2 rounded-lg bg-black/60 border border-[#2A2545] text-white"
              />
            </div>

            <div className="sm:col-span-3">
              <label className="text-slate-400 block mb-1">Descripción</label>
              <textarea
                rows={2}
                value={editingProd.description}
                onChange={(e) => setEditingProd({ ...editingProd, description: e.target.value })}
                className="w-full p-2 rounded-lg bg-black/60 border border-[#2A2545] text-white"
              />
            </div>

            <div className="flex items-center space-x-6 sm:col-span-3">
              <label className="flex items-center space-x-2 text-slate-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={editingProd.featured}
                  onChange={(e) => setEditingProd({ ...editingProd, featured: e.target.checked })}
                />
                <span>Destacado (Kaio-ken)</span>
              </label>

              <label className="flex items-center space-x-2 text-slate-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={editingProd.isNew}
                  onChange={(e) => setEditingProd({ ...editingProd, isNew: e.target.checked })}
                />
                <span>Nuevo</span>
              </label>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-black font-bold text-xs cursor-pointer"
          >
            {loading ? 'Actualizando...' : 'Guardar Cambios del Producto'}
          </button>
        </form>
      )}

      {/* Tabla de Productos Existentes */}
      <div className="rounded-2xl bg-[#12111F]/80 border border-[#2A2545] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs font-mono">
            <thead className="bg-black/50 text-slate-400 uppercase text-[10px] tracking-wider border-b border-[#2A2545]">
              <tr>
                <th className="px-4 py-3">IMAGEN</th>
                <th className="px-4 py-3">PRODUCTO</th>
                <th className="px-4 py-3">CATEGORÍA</th>
                <th className="px-4 py-3">PRECIO</th>
                <th className="px-4 py-3">ESTADO</th>
                <th className="px-4 py-3 text-right">ACCIONES</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#2A2545]/60 text-slate-300">
              {products.map((p) => {
                const img = p.images?.[0]?.url || 'https://images.unsplash.com/photo-1578632767115-351597cf2477?w=200';
                return (
                  <tr key={p.id} className="hover:bg-white/5 transition-colors">
                    <td className="px-4 py-2.5">
                      <img src={img} alt={p.name} className="w-10 h-10 object-cover rounded-md border border-white/10" />
                    </td>
                    <td className="px-4 py-2.5 font-bold text-white">
                      <div>{p.name}</div>
                      <span className="text-[10px] text-slate-400 font-normal">{p.franchise}</span>
                    </td>
                    <td className="px-4 py-2.5 text-emerald-400">{p.category.name}</td>
                    <td className="px-4 py-2.5 text-amber-400 font-bold">{formatPrice(Number(p.price))}</td>
                    <td className="px-4 py-2.5">
                      <div className="flex gap-1">
                        {p.featured && <span className="px-1.5 py-0.5 rounded bg-red-600/30 text-red-400 text-[9px]">KAIO-KEN</span>}
                        {p.isNew && <span className="px-1.5 py-0.5 rounded bg-amber-400/30 text-amber-300 text-[9px]">NUEVO</span>}
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-right space-x-2">
                      <button
                        onClick={() => {
                          setEditingProd(p);
                          setEditImageUrl(p.images?.[0]?.url || '');
                        }}
                        className="text-xs text-amber-400 hover:underline cursor-pointer"
                      >
                        Editar
                      </button>
                      <button
                        onClick={() => handleDeleteProduct(p.id)}
                        className="text-xs text-red-400 hover:underline cursor-pointer"
                      >
                        Eliminar
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
